<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onScopeDispose,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import V2ReservationPartyQuickRow from '@/components/v2/reservations/V2ReservationPartyQuickRow.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import {
    formatReservationPhoneInput,
    normalizeReservationPhone,
  } from '@/domain/reservations/reservation-phone';
  import { AlertType } from '@/interfaces/IAlert';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useReservationsStore } from '@/stores/reservations.store';

  const props = withDefaults(defineProps<{ partySize?: number }>(), {
    partySize: 2,
  });
  const restaurant = useRestaurantStore();
  const reservations = useReservationsStore();
  const modals = useModal();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const modalName = 'v2-reservation-waitlist-add';
  const restaurantId = restaurant.restaurant?.id;
  const branchId = restaurant.selectedBranch?.id;
  const country = restaurant.restaurant?.country ?? 'AR';
  const partySize = ref(Math.min(50, Math.max(1, Math.trunc(props.partySize))));
  const name = ref('');
  const phone = ref('');
  const quotedWaitMin = ref(20);
  const sectorId = ref('');
  const notes = ref('');
  const saving = ref(false);
  const submitted = ref(false);
  const error = ref('');
  const quotes = [5, 10, 15, 20, 30, 45];
  const sectors = computed(() =>
    (restaurant.sectors ?? [])
      .filter(
        (sector) =>
          sector.restaurantId === restaurantId && sector.branchId === branchId
      )
      .sort((a, b) => a.index - b.index)
  );
  const normalizedPhone = computed(() =>
    normalizeReservationPhone(phone.value, country)
  );
  const tr = (key: string) => String(proxy.$t(key));
  let alive = true;
  onScopeDispose(() => {
    alive = false;
  });
  const current = () =>
    alive &&
    restaurantId === restaurant.restaurant?.id &&
    branchId === restaurant.selectedBranch?.id;
  watch(
    () => [restaurant.restaurant?.id, restaurant.selectedBranch?.id],
    () => {
      if (!current()) modals.hide(modalName);
    }
  );
  function inputPhone(value: string) {
    phone.value = formatReservationPhoneInput(value, country);
  }
  async function save() {
    submitted.value = true;
    if (
      saving.value ||
      !current() ||
      !restaurantId ||
      !branchId ||
      !name.value.trim() ||
      !normalizedPhone.value ||
      !Number.isInteger(partySize.value) ||
      partySize.value < 1 ||
      partySize.value > 200
    )
      return;
    saving.value = true;
    error.value = '';
    try {
      const entry = await ReservationsService.addWaitlist({
        restaurantId,
        branchId,
        kind: 'walk_in',
        guestName: name.value.trim(),
        guestPhone: normalizedPhone.value,
        partySize: partySize.value,
        quotedWaitMin: quotedWaitMin.value,
        sectorId: sectorId.value === '' ? null : Number(sectorId.value),
        notes: notes.value.trim() || null,
      });
      if (!current()) return;
      reservations.revision += 1;
      ui.showToast(AlertType.Success, tr('reservations.waitlist.added'));
      proxy.$parent?.$emit('saved', entry);
      modals.hide(modalName);
    } catch (cause) {
      if (!current()) return;
      saving.value = false;
      error.value = tr('reservations.detail.saveError');
      ui.serverError(cause, error.value);
    }
  }
</script>

<template>
  <form class="waitlist-add-modal" @submit.prevent="save">
    <header>
      <h2>{{ $t('reservations.waitlist.form.title') }}</h2>
    </header>
    <div class="modal-content">
      <PosUiField :label="$t('reservations.form.party')" required
        ><V2ReservationPartyQuickRow v-model="partySize" :disabled="saving"
      /></PosUiField>
      <PosUiField
        :label="$t('reservations.form.name')"
        input-id="waitlist-guest-name"
        :error="
          submitted && !name.trim()
            ? $t('reservations.form.nameRequired')
            : undefined
        "
        required
        ><PosUiInput
          id="waitlist-guest-name"
          v-model="name"
          :maxlength="128"
          :disabled="saving"
      /></PosUiField>
      <PosUiField
        :label="$t('reservations.form.phone')"
        input-id="waitlist-guest-phone"
        :hint="$t('reservations.waitlist.form.phoneRequired')"
        :error="
          submitted && !normalizedPhone
            ? $t('reservations.form.phoneInvalid')
            : undefined
        "
        required
        ><PosUiInput
          id="waitlist-guest-phone"
          :value="phone"
          type="tel"
          inputmode="tel"
          icon="phone-outline"
          :maxlength="32"
          :disabled="saving"
          @input="inputPhone"
      /></PosUiField>
      <PosUiField :label="$t('reservations.waitlist.form.quoted')"
        ><div class="quote-options">
          <PosUiChip
            v-for="quote in quotes"
            :key="quote"
            :active="quote === quotedWaitMin"
            :disabled="saving"
            @click="quotedWaitMin = quote"
            >{{
              $t('reservations.common.minutes', { minutes: quote })
            }}</PosUiChip
          >
        </div></PosUiField
      >
      <PosUiField
        :label="$t('reservations.waitlist.form.sector')"
        input-id="waitlist-sector"
        ><select
          id="waitlist-sector"
          v-model="sectorId"
          class="sector-select"
          :disabled="saving"
        >
          <option value="">{{ $t('reservations.form.sectorAny') }}</option>
          <option
            v-for="sector in sectors"
            :key="sector.id"
            :value="String(sector.id)"
          >
            {{ sector.name }}
          </option>
        </select></PosUiField
      >
      <PosUiField
        :label="$t('reservations.waitlist.form.notes')"
        input-id="waitlist-notes"
        ><PosUiTextarea
          id="waitlist-notes"
          v-model="notes"
          :maxlength="255"
          :rows="2"
          :disabled="saving"
      /></PosUiField>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
    </div>
    <footer>
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="modals.hide(modalName)"
        >{{ $t('cancel') }}</PosUiButton
      ><PosUiButton type="submit" :disabled="saving">{{
        $t('reservations.waitlist.form.submit')
      }}</PosUiButton>
    </footer>
  </form>
</template>

<style scoped lang="scss">
  .waitlist-add-modal {
    @apply tw-flex tw-max-h-[85vh] tw-flex-col tw-bg-white;
    header {
      @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-p-5;
      h2 {
        @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }
    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-5 tw-overflow-y-auto tw-p-5;
    }
    .quote-options {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .sector-select {
      @apply tw-min-h-11 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-700 tw-outline-none focus:tw-border-orange-600 focus:tw-ring-1 focus:tw-ring-orange-600 disabled:tw-opacity-60;
    }
    .error {
      @apply tw-m-0 tw-text-sm tw-text-red-600;
    }
    footer {
      @apply tw-flex tw-shrink-0 tw-justify-end tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-p-4;
    }
  }
</style>
